import { IterableMap } from "@redotech/react-util/component";
import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import {
  ExternalShipmentTracker,
  Trackable,
  TrackableType,
} from "@redotech/redo-model/trackable";
import { Flex } from "@redotech/redo-web/flex";
import { replaceWordsByMerchantRequest } from "@redotech/redo-web/settings-elements/order/order-details";
import { Text } from "@redotech/redo-web/text";
import * as classNames from "classnames";
import { memo, useMemo, useState } from "react";
import { ReturnStepsCard } from "../../return-status/return-steps-card";
import * as trackingCss from "../order-tracking.module.css";
import * as fulfillmentCss from "./fulfillment.module.css";

export const TrackingDetails = memo(function TrackingDetails({
  trackable,
  trackingEvents,
  settings,
}: {
  trackable: Trackable;
  trackingEvents: any[];
  settings: ReturnAppSettings;
}) {
  const [width, _] = useState(window.innerWidth);
  const [showTrackingDetails, setShowTrackingDetails] = useState(width > 1000);

  const minHeight =
    trackable.type === TrackableType.ORDER
      ? Math.min(3, trackingEvents.length) * 34 +
        (Math.min(3, trackingEvents.length) - 1) * 8
      : 3 * 34 + 2 * 8;

  if (showTrackingDetails) {
    return (
      <div
        style={{ flex: 1, position: "relative", minHeight: `${minHeight}px` }}
      >
        <Flex
          bottom="0"
          dir="column"
          gap="md"
          left="0"
          overflowY="auto"
          position="absolute"
          reverse
          right="0"
          top="0"
        >
          {trackable.type === TrackableType.ORDER ? (
            <IterableMap items={trackingEvents} keyFn={(_, i) => i}>
              {(detail, index) => (
                <TrackingEventComponent
                  detail={detail}
                  key={index}
                  settings={settings}
                />
              )}
            </IterableMap>
          ) : (
            <ReturnStepsCard
              cardless
              settings={settings}
              trackable={trackable}
            />
          )}
        </Flex>
      </div>
    );
  }

  return null;
});

const TrackingEventComponent = memo(function TrackingEventComponent({
  detail,
  settings,
}: {
  detail: ExternalShipmentTracker["tracking_details"][number];
  settings: ReturnAppSettings;
}) {
  const date = useMemo(() => {
    try {
      if (!detail.datetime) return null;
      const date = Temporal.Instant.from(detail.datetime);
      const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return date.toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: clientTimeZone,
      });
    } catch (e) {
      console.error("Error parsing date", e);
      return "Unknown";
    }
  }, [detail.datetime]);

  const message = useMemo(() => {
    const { city, state, country } = detail.tracking_location ?? {};
    const locationParts = [];

    if (city) locationParts.push(city);

    if (state) locationParts.push(state);
    if (country) locationParts.push(country);

    const location =
      locationParts.length > 0 ? `, ${locationParts.join(", ")}` : "";

    return `${detail.message}${location}`;
  }, [detail]);

  const showFilledCircle: boolean = "filled" in detail && !!detail.filled;

  return (
    <Flex
      className={fulfillmentCss.historyEntry}
      dir="column"
      gap="xxs"
      justify="center"
      pl="3xl"
      position="relative"
    >
      {showFilledCircle && <span className={fulfillmentCss.filledCircle} />}
      {date && (
        <Text className={trackingCss.tinyText} fontWeight="semibold">
          {date}
        </Text>
      )}
      <span
        className={classNames(
          trackingCss.xTinyText,
          trackingCss.thin,
          trackingCss.grey,
        )}
      >
        {replaceWordsByMerchantRequest(message, settings.storeUrl)}
      </span>
    </Flex>
  );
});
